import { useEffect, useState } from "react";

import { isUUID, logError, SDKError } from "@smart/itops-utils-basic";

import {
  LayoutContact,
  LayoutMatterItem,
  UpdateLayoutMatterItemRequest,
  UseSDKApiOptions,
} from "../types";

export const useLayouts = ({ client, isInit, observe }: UseSDKApiOptions) => {
  const [currentLayouts, setCurrentLayouts] = useState<LayoutMatterItem[]>();
  const shouldObserve = observe?.includes("layouts");

  useEffect(() => {
    if (isInit && client && shouldObserve && client.context?.matterId) {
      client.layouts
        .get(client.context.matterId)
        .then((layoutMatter) => setCurrentLayouts(layoutMatter.value))
        .catch((err) =>
          logError(
            new SDKError(err, {
              action: "layouts.get",
              input: { matterId: client.context.matterId },
            }),
          ),
        );
      client.layouts.observe((layoutMatter) =>
        setCurrentLayouts(layoutMatter.value),
      );
    }
  }, [isInit, client?.context?.matterId, shouldObserve]);

  if (!isInit || !client) return undefined;

  return {
    get: async (matterId?: string) => {
      try {
        // eslint-disable-next-line @typescript-eslint/return-await
        return await client.layouts.get(matterId);
      } catch (error) {
        throw new SDKError(error, {
          action: "layouts.get",
          input: { matterId },
        });
      }
    },
    update: async ({
      itemId,
      layoutId,
      matterId,
      values,
    }: UpdateLayoutMatterItemRequest) => {
      try {
        let id: string | undefined;
        if (itemId) {
          id = itemId;
        } else if (layoutId) {
          const layout = currentLayouts?.find(
            (l) => l.layoutDesign.id === layoutId,
          );
          if (layout) id = layout.itemId;
        }

        if (!id) throw new Error("Item id not found");

        const input = {
          values,
          matterId,
          itemId: id,
        };

        const result = await client.layouts.update(input);
        const updatedLayout = result.value?.find((l) => l.itemId === id);
        const failedUpdates = values.filter(({ key, value }) => {
          const updatedValue = updatedLayout?.values.find(
            (updated) =>
              updated.key === key ||
              updated.key.includes(key) ||
              /**
               * The returned key may ignore [0] so we need to check the key without it.
               * E.g, the key is "Matter/Children[0]/ChildDetails/PrimaryCareGiver" but
               * the returned key may be "Matter/Children/ChildDetails/PrimaryCareGiver".
               */
              key.replace(/\[0\]/g, "") === updated.key,
          )?.value;
          return (
            (updatedValue ?? "").toLowerCase() !== (value ?? "").toLowerCase()
          );
        });
        if (failedUpdates.length) {
          throw new Error(
            `Fields did not update - ${failedUpdates
              .map((u) => u.key)
              .join(", ")}`,
          );
        }

        return result;
      } catch (error) {
        throw new SDKError(error, {
          action: "layouts.update",
          input: { itemId, layoutId, matterId, values },
        });
      }
    },
    getContacts: async (itemId: string) => {
      /**
       * At the moment the SDK getContacts function does not get
       * the layout contacts for custom layouts. The getLayoutContactsUsingRoleAndRelationship
       * function is a workaround to get the layout contacts for all layouts.
       */
      try {
        // eslint-disable-next-line @typescript-eslint/return-await
        return await client.layouts.getContacts(itemId);
      } catch (error) {
        throw new SDKError(error, {
          action: "layouts.getContacts",
          input: { itemId },
        });
      }
    },
    getLayoutContactsUsingRoleAndRelationship: async (
      itemId: string,
    ): Promise<{ layoutContacts: LayoutContact[] }> => {
      const layoutItem = currentLayouts?.find((l) => l.itemId === itemId);
      if (!layoutItem) return { layoutContacts: [] };
      /**
       * We can't get the layout design with the SDK to know which key is a layout contact
       * so we need to manually check the role/relationship id.
       */
      const layoutContactValues = layoutItem.values.filter((v) =>
        isUUID(v.value),
      );
      const roles = await client.roles.get();
      if (!layoutItem.parentId) {
        // If it's a top level layout, the id is the role id
        const layoutContacts = layoutContactValues
          .map(({ key, value }) => {
            const role = roles.roles.find((r) => r.id === value);
            return { key, contactId: role?.contactId };
          })
          .filter(({ contactId }) => contactId) as {
          key: string;
          contactId: string;
        }[];
        return { layoutContacts };
      }
      // If it's a sub layout, the id is the relationship id
      const relationships = (
        await Promise.all(
          roles.roles.map((role) => client.relationships.get(role.id)),
        )
      ).flatMap((r) => r.relationships);

      const layoutContacts = layoutContactValues
        .map(({ key, value }) => {
          const relationship = relationships.find((r) => r.id === value);
          return { key, contactId: relationship?.contactId };
        })
        .filter(({ contactId }) => contactId) as {
        key: string;
        contactId: string;
      }[];
      return { layoutContacts };
    },
    current: currentLayouts,
  };
};
